import {mapOpenWorldResponseSlot,mapPendingOpenWorldResponse} from '@/api/mappers/openWorldResponses'
import { validOpenWorldConversationSettings, validOpenWorldDirectorNote, validOpenWorldConversationChange, type ChangeOpenWorldConversationRequest } from '@/lib/openWorlds/conversation'
import { supabase } from '@/lib/supabaseClient'
import { mapOpenWorldNoteTurn } from '@/api/adapters/supabase/open-world-notes'
import { requireOpenWorldSession } from '@/api/adapters/supabase/open-world-auth'
import {
  isOpenWorldJournalJson,
  isOpenWorldJournalPositiveInteger,
  isOpenWorldJournalRecord,
  isOpenWorldJournalSafeError,
  isOpenWorldJournalUuid,
  validateFinishOpenWorldJournalRequest,
  validatePublishOpenWorldAttemptRequest,
  validateStartOpenWorldJournalRequest,
} from '@/lib/openWorlds/journalPersistence'
import type {
  FinishOpenWorldJournalRequest,
  FinishOpenWorldJournalResponse,
  GetOpenWorldActiveBranchResponse,
  GetOpenWorldJournalResponse,
  OpenWorldJournalRecord,
  OpenWorldPublicationAcknowledgement,
  PublishOpenWorldAttemptRequest,
  PublishOpenWorldAttemptResponse,
  StartOpenWorldJournalRequest,
  StartOpenWorldJournalResponse,
} from '@/lib/openWorlds/journalPersistence'
import type {
  OpenWorldCompletedAttempt,
  OpenWorldGeneratedEntry,
  OpenWorldRuntimeState,
} from '@/lib/openWorlds/types'
import { OpenWorldPersistenceError } from '@/lib/openWorlds/persistence'
import type { PlaythroughExecutionState } from '@/lib/openWorlds/attemptPersistence'
import {
  validMessageEdit, validStoryMutation, validPlayerMessageChange, validNoteEdit, validNoteTakingChange,
  type EditOpenWorldMessageRequest, type EditOpenWorldNoteRequest, type RewindOpenWorldStoryRequest,
  type OpenWorldStorySnapshot, type OpenWorldStoryMutationResponse, type OpenWorldStoryChangeRequest,
  type ChangeOpenWorldPlayerMessageRequest, type OpenWorldPlayerMessage, type SetOpenWorldNoteTakingRequest,
} from '@/lib/openWorlds/storyMutations'

type UnknownRecord = Record<string, unknown>
const JOURNAL_SELECT = [
  'preparation_source_record_id', 'forked_from_id', 'id', 'attempt_id', 'sequence', 'kind', 'phase', 'parent_id', 'name', 'request',
  'status', 'outcome', 'error', 'usage', 'elapsed_ms', 'started_at', 'finished_at',
].join(', ')
const BRANCH_SELECT = [
  'id', 'parent_attempt_id', 'turn_number', 'user_message', 'storyteller_message',
  'generated_entries', 'state_after',
].join(', ')
const TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/

function backend(path: string): never {
  throw new OpenWorldPersistenceError('backend', `Malformed Open World journal response at ${path}.`)
}
function exact(value: unknown, keys: readonly string[], path: string): UnknownRecord {
  if (!isOpenWorldJournalRecord(value)) backend(path)
  const actual = Object.keys(value)
  if (actual.length !== keys.length || keys.some((key) => !Object.prototype.hasOwnProperty.call(value, key))) backend(path)
  return value
}
function uuid(value: unknown, path: string): string {
  if (!isOpenWorldJournalUuid(value)) backend(path)
  return value
}
function nullableUuid(value: unknown, path: string): string | null {
  if (value === null) return null
  return uuid(value, path)
}
function integer(value: unknown, path: string, allowZero = false): number {
  if (allowZero && value === 0) return 0
  if (!isOpenWorldJournalPositiveInteger(value)) backend(path)
  return value
}
function timestamp(value: unknown, path: string): string {
  if (typeof value !== 'string' || !TIMESTAMP_RE.test(value) || !Number.isFinite(Date.parse(value))) backend(path)
  return value
}
function nullableTimestamp(value: unknown, path: string): string | null {
  return value === null ? null : timestamp(value, path)
}
function json(value: unknown, path: string) {
  if (!isOpenWorldJournalJson(value)) backend(path)
  return structuredClone(value)
}
function error(value: unknown, path: string) {
  if (!isOpenWorldJournalSafeError(value)) backend(path)
  return value === null ? null : { code: value.code, message: value.message }
}
function mapRecord(value: unknown, path = 'record', rpc = false): OpenWorldJournalRecord {
  const keys = [
    'id', 'attemptId', 'sequence', 'kind', 'phase', 'parentId', 'name', 'request',
    'status', 'outcome', 'error', 'usage', 'elapsedMs', 'startedAt', 'finishedAt',
  ]
  const snake = [
    'id', 'attempt_id', 'sequence', 'kind', 'phase', 'parent_id', 'name', 'request',
    'status', 'outcome', 'error', 'usage', 'elapsed_ms', 'started_at', 'finished_at',
  ]
  const expected = rpc ? keys : snake
  if (!rpc && isOpenWorldJournalRecord(value)) {
    for (const key of ['forked_from_id','preparation_source_record_id']) if (Object.hasOwn(value,key)) expected.push(key)
  }
  const row = exact(value, expected, path)
  const get = (camel: string, database: string) => row[rpc ? camel : database]
  const kind = get('kind', 'kind')
  const phase = get('phase', 'phase')
  const status = get('status', 'status')
  if (!['phase', 'model', 'tool'].includes(kind as string)) backend(`${path}.kind`)
  if (!['curator', 'synthesis', 'storyteller'].includes(phase as string)) backend(`${path}.phase`)
  if (!['started', 'completed', 'skipped', 'handled_error', 'failed', 'aborted'].includes(status as string)) backend(`${path}.status`)
  const name = get('name', 'name')
  const elapsed = get('elapsedMs', 'elapsed_ms')
  if (typeof name !== 'string' || name.length === 0) backend(`${path}.name`)
  if (elapsed !== null && (!Number.isInteger(elapsed) || (elapsed as number) < 0)) backend(`${path}.elapsedMs`)
  return {
    sourceRecordId: rpc ? null : nullableUuid(row.preparation_source_record_id ?? row.forked_from_id ?? null, `${path}.sourceRecordId`),
    id: uuid(row.id, `${path}.id`),
    attemptId: uuid(get('attemptId', 'attempt_id'), `${path}.attemptId`),
    sequence: integer(row.sequence, `${path}.sequence`),
    kind: kind as OpenWorldJournalRecord['kind'],
    phase: phase as OpenWorldJournalRecord['phase'],
    parentId: nullableUuid(get('parentId', 'parent_id'), `${path}.parentId`),
    name,
    request: json(row.request, `${path}.request`),
    status: status as OpenWorldJournalRecord['status'],
    outcome: row.outcome === null ? null : json(row.outcome, `${path}.outcome`),
    error: error(row.error, `${path}.error`),
    usage: row.usage === null ? null : json(row.usage, `${path}.usage`),
    elapsedMs: elapsed as number | null,
    startedAt: timestamp(get('startedAt', 'started_at'), `${path}.startedAt`),
    finishedAt: nullableTimestamp(get('finishedAt', 'finished_at'), `${path}.finishedAt`),
  }
}

function mapBackendError(cause: unknown): OpenWorldPersistenceError {
  if (cause instanceof OpenWorldPersistenceError) return cause
  const record = isOpenWorldJournalRecord(cause) ? cause : {}
  const message = typeof record.message === 'string' ? record.message : 'Open World persistence failed.'
  const match = message.match(/OW_(UNAUTHENTICATED|INVALID_INPUT|NOT_FOUND|CONFLICT)/)
  return new OpenWorldPersistenceError(
    match ? match[1]!.toLowerCase() as 'unauthenticated' | 'invalid_input' | 'not_found' | 'conflict' : 'backend',
    message,
    { cause },
  )
}
async function rpc(name: string, parameters: UnknownRecord): Promise<unknown> {
  await requireOpenWorldSession()
  const { data, error } = await supabase.rpc(name, parameters)
  if (error) throw mapBackendError(error)
  return data
}

export async function startOpenWorldJournal(
  request: StartOpenWorldJournalRequest,
): Promise<StartOpenWorldJournalResponse> {
  if (!validateStartOpenWorldJournalRequest(request)) throw new OpenWorldPersistenceError('invalid_input', 'Open World journal start is invalid.')
  const row = exact(await rpc('rpc_start_open_world_journal_v1', {
    p_id: request.id, p_attempt_id: request.attemptId, p_execution_token: request.executionToken,
    p_sequence: request.sequence, p_kind: request.kind, p_phase: request.phase,
    p_parent_id: request.parentId, p_name: request.name, p_request: request.request,
  }), ['record', 'replayed'], 'response')
  if (typeof row.replayed !== 'boolean') backend('response.replayed')
  return { record: mapRecord(row.record, 'record', true), replayed: row.replayed }
}

export async function finishOpenWorldJournal(
  request: FinishOpenWorldJournalRequest,
): Promise<FinishOpenWorldJournalResponse> {
  if (!validateFinishOpenWorldJournalRequest(request)) throw new OpenWorldPersistenceError('invalid_input', 'Open World journal outcome is invalid.')
  const row = exact(await rpc('rpc_finish_open_world_journal_v1', {
    p_id: request.id, p_attempt_id: request.attemptId, p_execution_token: request.executionToken,
    p_receipt_id: request.receiptId, p_status: request.status, p_outcome: request.outcome,
    p_error: request.error, p_usage: request.usage, p_elapsed_ms: request.elapsedMs,
  }), ['record', 'replayed'], 'response')
  if (typeof row.replayed !== 'boolean') backend('response.replayed')
  return { record: mapRecord(row.record, 'record', true), replayed: row.replayed }
}

function mapState(value: unknown): PlaythroughExecutionState {
  const row = exact(value, ['id', 'revision', 'activeHeadId', 'pendingAttemptId', 'updatedAt'], 'playthroughState')
  return { id: uuid(row.id, 'playthroughState.id'), revision: integer(row.revision, 'playthroughState.revision'),
    activeHeadId: nullableUuid(row.activeHeadId, 'playthroughState.activeHeadId'),
    pendingAttemptId: nullableUuid(row.pendingAttemptId, 'playthroughState.pendingAttemptId'),
    updatedAt: timestamp(row.updatedAt, 'playthroughState.updatedAt') }
}
function mapPublicationAck(value: unknown): OpenWorldPublicationAcknowledgement {
  const row = exact(value, ['attemptId', 'status', 'finishedAt', 'playthroughRevision', 'activeHeadId'], 'acknowledgement')
  if (row.status !== 'completed') backend('acknowledgement.status')
  return { attemptId: uuid(row.attemptId, 'acknowledgement.attemptId'), status: 'completed',
    finishedAt: timestamp(row.finishedAt, 'acknowledgement.finishedAt'),
    playthroughRevision: integer(row.playthroughRevision, 'acknowledgement.playthroughRevision'),
    activeHeadId: uuid(row.activeHeadId, 'acknowledgement.activeHeadId') }
}
export async function publishOpenWorldAttempt(
  request: PublishOpenWorldAttemptRequest,
): Promise<PublishOpenWorldAttemptResponse> {
  if (!validatePublishOpenWorldAttemptRequest(request)) throw new OpenWorldPersistenceError('invalid_input', 'Open World publication is invalid.')
  const row = exact(await rpc('rpc_publish_open_world_attempt_v1', {
    p_attempt_id: request.attemptId, p_execution_token: request.executionToken,
    p_receipt_id: request.receiptId, p_storyteller_message: request.storytellerMessage,
    p_generated_entries: request.generatedEntries, p_state_after: request.stateAfter,
    p_diagnostic_summary: request.diagnosticSummary,
  }), ['acknowledgement', 'playthroughState', 'replayed'], 'response')
  if (typeof row.replayed !== 'boolean') backend('response.replayed')
  return { acknowledgement: mapPublicationAck(row.acknowledgement),
    playthroughState: mapState(row.playthroughState), replayed: row.replayed }
}

export async function getOpenWorldJournal(attemptId: string): Promise<GetOpenWorldJournalResponse> {
  if (!isOpenWorldJournalUuid(attemptId)) throw new OpenWorldPersistenceError('invalid_input', 'Attempt ID must be a UUID.')
  await requireOpenWorldSession()
  const { data, error } = await supabase.from('open_world_journal').select(JOURNAL_SELECT)
    .eq('attempt_id', attemptId).order('sequence', { ascending: true })
  if (error) throw mapBackendError(error)
  if (!Array.isArray(data)) backend('items')
  return { items: data.map((item, index) => mapRecord(item, `items[${index}]`)) }
}

function mapCompletedAttempt(value: unknown, path: string): OpenWorldCompletedAttempt {
  const row = exact(value, ['id', 'parent_attempt_id', 'turn_number', 'user_message', 'storyteller_message', 'generated_entries', 'state_after',
    ...(isOpenWorldJournalRecord(value) && 'player_message_id' in value ? ['player_message_id'] : [])], path)
  if (typeof row.user_message !== 'string' || typeof row.storyteller_message !== 'string') backend(path)
  if (!Array.isArray(row.generated_entries) || !isOpenWorldJournalRecord(row.state_after)) backend(path)
  const generated = row.generated_entries as unknown as OpenWorldGeneratedEntry[]
  const state = row.state_after as unknown as OpenWorldRuntimeState
  return { id: uuid(row.id, `${path}.id`),
    ...(row.player_message_id !== undefined ? { playerMessageId: uuid(row.player_message_id, `${path}.playerMessageId`) } : {}), parentAttemptId: nullableUuid(row.parent_attempt_id, `${path}.parentAttemptId`),
    turnNumber: integer(row.turn_number, `${path}.turnNumber`), userMessage: row.user_message,
    storytellerMessage: row.storyteller_message, generatedEntries: structuredClone(generated), stateAfter: structuredClone(state) }
}
export async function getOpenWorldActiveBranch(playthroughId: string): Promise<GetOpenWorldActiveBranchResponse> {
  if (!isOpenWorldJournalUuid(playthroughId)) throw new OpenWorldPersistenceError('invalid_input', 'Playthrough ID must be a UUID.')
  await requireOpenWorldSession()
  const { data, error } = await supabase.from('open_world_attempts').select(BRANCH_SELECT)
    .eq('playthrough_id', playthroughId).eq('status', 'completed').order('turn_number', { ascending: true })
  if (error) throw mapBackendError(error)
  if (!Array.isArray(data)) backend('attempts')
  return { attempts: data.map((item, index) => mapCompletedAttempt(item, `attempts[${index}]`)) }
}

export function mapStorySnapshot(value: unknown): OpenWorldStorySnapshot {
  const row = exact(value, ['playthroughState', 'attempts', 'messageEdits', 'awaitingPlayerMessage',
    ...(isOpenWorldJournalRecord(value) && 'noteTurns' in value ? ['noteTurns'] : []),
    ...(isOpenWorldJournalRecord(value) && 'noteTakingPaused' in value ? ['noteTakingPaused'] : []),
    ...(isOpenWorldJournalRecord(value) && 'noteResumePending' in value ? ['noteResumePending'] : []),
    ...['responseSlots','pendingResponse','conversationSettings','directorNotes','displayTitle','openingOverride'].filter((key) => isOpenWorldJournalRecord(value) && key in value)], 'snapshot')
  if (!Array.isArray(row.attempts) || !Array.isArray(row.messageEdits)) backend('snapshot')
  if (row.noteTurns !== undefined && !Array.isArray(row.noteTurns)) backend('snapshot.noteTurns')
  if (row.noteTakingPaused !== undefined && typeof row.noteTakingPaused !== 'boolean') backend('snapshot.noteTakingPaused')
  if (row.noteResumePending !== undefined && (typeof row.noteResumePending !== 'boolean' ||
    (row.noteResumePending && row.noteTakingPaused !== true))) backend('snapshot.noteResumePending')
  if (row.conversationSettings !== undefined && !validOpenWorldConversationSettings(row.conversationSettings)) backend('snapshot.conversationSettings')
  for (const key of ['displayTitle','openingOverride']) {
    if (row[key] !== undefined && row[key] !== null && (typeof row[key] !== 'string' || !row[key].trim())) backend(`snapshot.${key}`)
  }
  if (row.directorNotes !== undefined && !Array.isArray(row.directorNotes)) backend('snapshot.directorNotes')
  if (row.responseSlots !== undefined && !Array.isArray(row.responseSlots)) backend('snapshot.responseSlots')
  return {
    ...(row.responseSlots === undefined ? {} : {responseSlots:(row.responseSlots as unknown[]).map(mapOpenWorldResponseSlot)}),
    ...(row.pendingResponse === undefined ? {} : {pendingResponse:row.pendingResponse === null ? null : mapPendingOpenWorldResponse(row.pendingResponse)}),
    ...(row.conversationSettings === undefined ? {} : { conversationSettings: structuredClone(row.conversationSettings) }),
    ...(row.displayTitle === undefined ? {} : { displayTitle: row.displayTitle as string | null }),
    ...(row.openingOverride === undefined ? {} : { openingOverride: row.openingOverride as string | null }),
    ...(row.directorNotes === undefined ? {} : { directorNotes: (row.directorNotes as unknown[]).map((item, index) => {
      const path = `snapshot.directorNotes[${index}]`
      const note = exact(item, ['playerMessageId','content','placement','visibility_count','updatedAt'], path)
      const value = {content: note.content, placement: note.placement, visibility_count: note.visibility_count}
      if (!validOpenWorldDirectorNote(value)) backend(path)
      return {...value, playerMessageId: uuid(note.playerMessageId, path), updatedAt: timestamp(note.updatedAt, path)}
    }) }),
    ...(row.noteTakingPaused === undefined ? {} : { noteTakingPaused: row.noteTakingPaused as boolean }),
    ...(row.noteResumePending === undefined ? {} : { noteResumePending: row.noteResumePending as boolean }),
    noteTurns: row.noteTurns === undefined ? [] : (row.noteTurns as unknown[]).map(mapOpenWorldNoteTurn),
    playthroughState: mapState(row.playthroughState),
    awaitingPlayerMessage: row.awaitingPlayerMessage === null ? null : mapPlayerMessage(row.awaitingPlayerMessage),
    attempts: row.attempts.map((item, index) => mapCompletedAttempt(item, `snapshot.attempts[${index}]`)),
    messageEdits: row.messageEdits.map((item, index) => {
      const path = `snapshot.messageEdits[${index}]`
      const edit = exact(item, ['attemptId', 'role', 'content', 'updatedAt'], path)
      if (!['player', 'storyteller'].includes(edit.role as string) || typeof edit.content !== 'string' || !edit.content.trim()) backend(path)
      return { attemptId: uuid(edit.attemptId, `${path}.attemptId`),
        role: edit.role as 'player' | 'storyteller', content: edit.content as string,
        updatedAt: timestamp(edit.updatedAt, `${path}.updatedAt`) }
    }),
  }
}

export async function getOpenWorldStorySnapshot(playthroughId: string): Promise<OpenWorldStorySnapshot> {
  if (!isOpenWorldJournalUuid(playthroughId)) throw new OpenWorldPersistenceError('invalid_input', 'Playthrough ID must be a UUID.')
  return mapStorySnapshot(await rpc('rpc_get_open_world_story_snapshot_v5', { p_playthrough_id: playthroughId }))
}

function mapPlayerMessage(value: unknown): OpenWorldPlayerMessage {
  const path = 'snapshot.awaitingPlayerMessage'
  const row = exact(value, ['id', 'parentAttemptId', 'content', 'createdAt', 'updatedAt'], path)
  if (typeof row.content !== 'string' || !row.content.trim() || new TextEncoder().encode(row.content).length > 65_536) backend(path)
  return { id: uuid(row.id, `${path}.id`), parentAttemptId: nullableUuid(row.parentAttemptId, `${path}.parentAttemptId`),
    content: row.content, createdAt: timestamp(row.createdAt, `${path}.createdAt`), updatedAt: timestamp(row.updatedAt, `${path}.updatedAt`) }
}

async function mutateStory(
  request: OpenWorldStoryChangeRequest, operation: OpenWorldStoryMutationResponse['acknowledgement']['operation'],
): Promise<OpenWorldStoryMutationResponse> {
  const note = 'operation' in request && request.operation === 'edit-note' ? request : null
  const taking = 'operation' in request && (request.operation === 'pause-notes' || request.operation === 'resume-notes') ? request : null
  const player = 'operation' in request && ['post-player', 'edit-player', 'delete-player'].includes(request.operation)
    ? request as ChangeOpenWorldPlayerMessageRequest : null
  const edit = 'role' in request ? request : null
  if (!(taking ? validNoteTakingChange(taking) : note ? validNoteEdit(note) : player ? validPlayerMessageChange(player) : edit ? validMessageEdit(edit) : validStoryMutation(request as RewindOpenWorldStoryRequest))) {
    throw new OpenWorldPersistenceError('invalid_input', 'Open World story change is invalid.')
  }
  const row = exact(await (taking ? rpc('rpc_set_open_world_note_taking_v2', {
    p_playthrough_id: taking.playthroughId, p_receipt_id: taking.receiptId,
    p_expected_revision: taking.expectedPlaythroughRevision, p_expected_head_id: taking.expectedHeadId,
    p_paused: taking.operation === 'pause-notes',
  }) : note ? rpc(note.noteId ? 'rpc_edit_open_world_note_v2' : 'rpc_edit_open_world_note_v1', {
    p_playthrough_id: note.playthroughId, p_receipt_id: note.receiptId,
    p_expected_revision: note.expectedPlaythroughRevision, p_expected_head_id: note.expectedHeadId,
    ...(note.noteId ? {p_note_id:note.noteId} : {p_attempt_id: note.attemptId}), p_note: note.content,
  }) : rpc('rpc_mutate_open_world_story_v2', {
    p_playthrough_id: request.playthroughId, p_receipt_id: request.receiptId,
    p_expected_revision: request.expectedPlaythroughRevision, p_expected_head_id: request.expectedHeadId,
    p_attempt_id: 'attemptId' in request ? request.attemptId : null,
    p_player_message_id: player?.playerMessageId ?? null,
    p_operation: operation,
    p_role: edit?.role ?? ('retainPlayerMessage' in request && request.retainPlayerMessage ? 'storyteller' : null),
    p_content: 'content' in request ? request.content ?? null : null,
  })), ['acknowledgement', 'snapshot', 'replayed'], 'response')
  const ack = exact(row.acknowledgement, ['receiptId', 'operation', 'playthroughRevision', 'activeHeadId'], 'acknowledgement')
  if (typeof row.replayed !== 'boolean' || ack.operation !== operation || ack.receiptId !== request.receiptId) backend('acknowledgement')
  return { acknowledgement: { receiptId: uuid(ack.receiptId, 'acknowledgement.receiptId'), operation,
    playthroughRevision: integer(ack.playthroughRevision, 'acknowledgement.playthroughRevision'),
    activeHeadId: nullableUuid(ack.activeHeadId, 'acknowledgement.activeHeadId') },
    snapshot: mapStorySnapshot(row.snapshot), replayed: row.replayed }
}

export const editOpenWorldNote = (request: EditOpenWorldNoteRequest) => mutateStory(request, 'edit-note')
export const setOpenWorldNoteTaking = (request: SetOpenWorldNoteTakingRequest) => mutateStory(request, request.operation)
export const editOpenWorldMessage = (request: EditOpenWorldMessageRequest) => mutateStory(request, 'edit')
export const rewindOpenWorldStory = (request: RewindOpenWorldStoryRequest) => mutateStory(request, 'rewind')
export const changeOpenWorldPlayerMessage = (request: ChangeOpenWorldPlayerMessageRequest) => mutateStory(request, request.operation)

export async function changeOpenWorldConversation(request: ChangeOpenWorldConversationRequest): Promise<OpenWorldStoryMutationResponse> {
  if (!validOpenWorldConversationChange(request)) throw new OpenWorldPersistenceError('invalid_input', 'Conversation change is invalid.')
  const row = exact(await (request.operation === 'select-response' ? rpc('rpc_select_open_world_response_v1', {
    p_playthrough_id:request.playthroughId,p_attempt_id:request.payload.attemptId,p_variant_id:request.payload.variantId,
    p_receipt_id:request.receiptId,p_expected_revision:request.expectedPlaythroughRevision,
  }) : rpc('rpc_mutate_open_world_conversation_v1', {
    p_playthrough_id: request.playthroughId, p_receipt_id: request.receiptId,
    p_expected_revision: request.expectedPlaythroughRevision, p_expected_head_id: request.expectedHeadId,
    p_operation: request.operation, p_payload: request.payload,
  })), ['acknowledgement','snapshot','replayed'], 'response')
  const ack = exact(row.acknowledgement, ['receiptId','operation','playthroughRevision','activeHeadId'], 'acknowledgement')
  if (row.replayed !== true && row.replayed !== false || ack.receiptId !== request.receiptId || ack.operation !== request.operation) backend('acknowledgement')
  return {acknowledgement: {receiptId: request.receiptId, operation: request.operation,
    playthroughRevision: integer(ack.playthroughRevision, 'acknowledgement.playthroughRevision'),
    activeHeadId: nullableUuid(ack.activeHeadId, 'acknowledgement.activeHeadId')},
    snapshot: mapStorySnapshot(row.snapshot), replayed: row.replayed}
}
